/**
 * buildAppPath — shared app route path builder
 *
 * Returns the relative path for a given entity type and optional ID.
 * Used by in-app skills (navigateTo) and as the core of the full-URL
 * buildAppUrl in functions-ai.
 *
 * Example: buildAppPath({ entity: 'employee', id: 'emp_123', tab: 'onboarding' })
 *          → '/hr/employees/emp_123?tab=onboarding'
 */

export type AppEntity =
  | 'employee'
  | 'lead'
  | 'contact'
  | 'asset'
  | 'ticket'
  | 'fsr'
  | 'invoice'
  | 'project'
  | 'post'
  | 'submission'
  | 'hr-templates'
  | 'hr-dashboard'
  | 'billing-dashboard'
  | 'crm-leads'
  | 'assets-dashboard'
  | 'field-service'
  | 'marketing-review';

export interface BuildAppPathOptions {
  entity: AppEntity;
  id?: string;
  tab?: string;
}

// ============================================================================
// ID VALIDATION
// ============================================================================

const ENTITY_ID_PATTERN = /^[a-zA-Z0-9_\-]{1,256}$/;

// ============================================================================
// ROUTE MAP
// ============================================================================

type RouteResolver = (id?: string) => string;

const ROUTE_MAP: Record<AppEntity, RouteResolver> = {
  employee: (id) => (id ? `/hr/employees/${id}` : '/hr/employees'),
  lead: (id) => (id ? `/crm/leads/${id}` : '/crm/leads'),
  contact: (id) => (id ? `/crm/contacts/${id}` : '/crm/contacts'),
  asset: (id) => (id ? `/assets/inventory/${id}` : '/assets/inventory'),
  ticket: (id) => (id ? `/field-service/tickets/${id}` : '/field-service'),
  fsr: (id) => (id ? `/field-service/reports/${id}` : '/field-service/reports'),
  invoice: (id) => (id ? `/billing/invoicing/${id}` : '/billing/invoicing'),
  project: (id) => (id ? `/projects/${id}` : '/projects'),
  post: (id) => (id ? `/marketing/content/composer/${id}` : '/marketing/content/composer'),
  submission: (id) => (id ? `/forms/submissions/${id}` : '/forms/submissions'),
  'hr-templates': () => '/hr/templates',
  'hr-dashboard': () => '/hr',
  'billing-dashboard': () => '/billing/dashboard',
  'crm-leads': () => '/crm/leads',
  'assets-dashboard': () => '/assets',
  'field-service': () => '/field-service',
  'marketing-review': () => '/marketing/review',
};

// ============================================================================
// PUBLIC API
// ============================================================================

export function buildAppPath(options: BuildAppPathOptions): string {
  const { entity, id, tab } = options;

  if (id !== undefined && id !== '') {
    if (!ENTITY_ID_PATTERN.test(id)) {
      throw new Error('buildAppPath: unsafe id value');
    }
  }

  const resolver = ROUTE_MAP[entity];
  const effectiveId = id === '' ? undefined : id;
  const basePath = resolver(effectiveId);

  if (tab !== undefined && tab !== '') {
    return `${basePath}?tab=${encodeURIComponent(tab)}`;
  }

  return basePath;
}
